/**
 * Global customer auth interceptor (Aug 2026 httpOnly cookie migration).
 *
 *  - Tokens live in httpOnly cookies; the browser attaches them
 *    automatically (axios.defaults.withCredentials = true, set in App.js).
 *    This interceptor never reads/writes a token itself.
 *  - On a 401 from a non-admin, non-pre-auth endpoint, attempts a silent
 *    refresh via POST /api/auth/refresh (which reads the refresh_token
 *    cookie server-side) and retries the original request once.
 *  - If refresh itself fails, wipes the cached customer profile and shows
 *    the soft re-login modal (customer routes) or silently rejects
 *    (admin/auth/landing routes).
 *
 * Call `installCustomerAuthInterceptor()` exactly once at app startup
 * from App.js.
 *
 * Coexistence with adminAuthInterceptor:
 *  - Both use the same global axios default instance. Each interceptor
 *    only handles requests whose URL falls in its own scope (admin vs
 *    everything else), so they don't fight each other on the same 401.
 */
import axios from 'axios';
import { CustomerStorage } from '../utils/storage';
import { isCustomerRoute, fireCustomerSessionExpired } from '../utils/sessionExpired';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_URL = `${API}/api/auth/refresh`;

// Pre-auth customer endpoints — a 401 here means "bad credentials", not
// "expired session". Attempting a refresh would just waste a round trip.
const CUSTOMER_PUBLIC_AUTH_PATHS = [
  '/auth/login', '/auth/signup', '/auth/guest', '/auth/verify-email',
  '/auth/resend-verification', '/auth/request-otp', '/auth/forgot-password',
  '/auth/reset-password', '/auth/refresh', '/auth/google',
];

// Dedup concurrent refresh attempts so parallel 401s trigger one refresh.
let refreshPromise = null;

/**
 * "Soft expiry" path: instead of hard-redirecting to /auth (which loses
 * the customer's in-flight work — e.g. their Step-3 booking selections),
 * we fire `customer-session-expired` so the `<SessionExpiredModal />`
 * overlay can prompt re-login inline. The original failing axios request
 * is queued via the event detail; after successful re-login the modal
 * dispatches `customer-session-restored` and the promise here resolves
 * with the retried response.
 *
 * If the customer dismisses the modal (cancel/escape), the event listener
 * receives `customer-session-expired-dismissed` and rejects the original
 * promise so existing error UI still fires.
 *
 * On NON-customer routes (admin, /auth, /) we silently reject — admins
 * have their own surface and shouldn't see a customer-themed modal.
 */
const showSoftExpiryModal = (config, originalError) => {
  if (!isCustomerRoute()) {
    return Promise.reject(originalError);
  }
  return new Promise((resolve, reject) => {
    const onRestored = () => {
      window.removeEventListener('customer-session-restored', onRestored);
      window.removeEventListener('customer-session-expired-dismissed', onDismissed);
      if (!config) return resolve(undefined);
      // The freshly-minted cookie is already attached by the browser.
      config._customerRetried = true;
      axios(config).then(resolve).catch(reject);
    };
    const onDismissed = () => {
      window.removeEventListener('customer-session-restored', onRestored);
      window.removeEventListener('customer-session-expired-dismissed', onDismissed);
      reject(originalError);
    };
    window.addEventListener('customer-session-restored', onRestored);
    window.addEventListener('customer-session-expired-dismissed', onDismissed);
    fireCustomerSessionExpired(config);
  });
};

const runRefresh = async () => {
  // Use `_skipAuthRefresh` so this very call can't recurse through the
  // interceptor on its own 401. Body is empty — the refresh_token cookie
  // is read server-side.
  const res = await axios.post(
    REFRESH_URL,
    {},
    { headers: { 'Content-Type': 'application/json' }, _skipAuthRefresh: true }
  );
  return res.data; // { access_token, refresh_token, expires_in } — cookies already rotated server-side
};

export const installCustomerAuthInterceptor = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      // Not a 401, no config, already retried, or explicitly skipped
      if (
        !response ||
        response.status !== 401 ||
        !config ||
        config._customerRetried ||
        config._skipAuthRefresh
      ) {
        return Promise.reject(error);
      }

      // Admin-scoped calls are handled by adminAuthInterceptor.
      if (config.url && config.url.includes('/admin/')) {
        return Promise.reject(error);
      }

      // Pre-auth endpoints — a 401 here is "bad credentials", not
      // "expired session".
      if (config.url && CUSTOMER_PUBLIC_AUTH_PATHS.some((p) => config.url.includes(p))) {
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = runRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const data = await refreshPromise;

        // Tell AuthContext (or any listener) that we just rotated. AuthContext
        // re-arms its proactive-refresh timer + updates tokenExpiresAtRef so
        // we stop relying on 401-then-refresh on every API call after expiry.
        try {
          window.dispatchEvent(new CustomEvent('customer-token-refreshed', {
            detail: { expires_in: data.expires_in }
          }));
        } catch (_) { /* CustomEvent unsupported — no-op */ }

        // Retry original request — the rotated cookie is already attached.
        config._customerRetried = true;
        return axios(config);
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[customerAuthInterceptor] refresh failed:',
          refreshErr?.response?.status,
          refreshErr?.response?.data?.detail || refreshErr?.message
        );

        // Soft landing for deactivated accounts. The refresh endpoint
        // returns 403 with "deactivated" in the detail when the customer
        // has been disabled by an admin (auth_routes.refresh_token).
        // Wipe session and hard-route to the explainer page — using
        // window.location to bypass any in-flight router state.
        const rs = refreshErr?.response?.status;
        const rd = refreshErr?.response?.data?.detail || '';
        if (rs === 403 && /deactivated/i.test(rd)) {
          CustomerStorage.clear();
          try {
            window.location.replace('/account-deactivated');
          } catch (_) { /* SSR or stripped window — non-fatal */ }
          return Promise.reject(refreshErr);
        }

        CustomerStorage.clear();
        return showSoftExpiryModal(config, error);
      }
    }
  );
};

export default installCustomerAuthInterceptor;
